import {
  onSnapshot,
  applySnapshot,
  Instance,
  IStateTreeNode,
} from 'mobx-state-tree';

import { LocalStorageService } from '@services';
import type {
  IMigration,
  SerializeType,
  DeserializeType,
} from '../migrations/MigrationTypes';

const PERSISTED_STATE_KEY = 'persist:';
const CURRENT_MIGRATION_INDEX = 'migration_index:persist:';

const defaultProcessor = (v: any) => v;

export class Persist<T extends IStateTreeNode> {
  private _key: string;
  private _store: Instance<T>;
  private _migrations: IMigration[];
  private _serialize: SerializeType = defaultProcessor;
  private _deserialize: DeserializeType = defaultProcessor;

  /**
   * Use `Persist.create` in order to create a persist.
   * Initialize all the variables.
   * @param {MSTStore} store Store we are going to persist
   * @param {String} key key indicates that persist. Will be used as a key in a store
   * @param {Array} migrations array of migrations
   */
  constructor(
    store: Instance<T>,
    key: string,
    migrations: IMigration[],
  ) {
    this._store = store;
    this._key = key;
    this._migrations = migrations;
  }

  /**
   * Creates a persist. Returns its instance.
   * Use `purge` to remove all the data from storage and `rehydrate` to fetch
   * snapshot from storage and apply it to the store.
   *
   * @param {MSTStore} store Store we are going to persist
   * @param {String} key key indicates that persist. Will be used as a key in a store
   * @param {Array} migrations array of migrations
   *
   * @returns {Object} instance of Persist.
   */
  static async create<TCreate extends IStateTreeNode>(
    store: Instance<TCreate>,
    key: string,
    migrations: IMigration[],
  ): Promise<Persist<TCreate>> {
    const instance = new Persist(store, key, migrations);

    await instance.initialize();

    return instance;
  }

  async initialize() {
    const { serialize, deserialize } =
      await this._useLatestMigration();

    this._serialize = serialize || defaultProcessor;
    this._deserialize = deserialize || defaultProcessor;

    this._recordSnapshots();
  }

  /**
   * This is persis key. This key is used to store data in a storage
   *
   * @readonly
   * @memberof Persist
   */
  get key() {
    return `${PERSISTED_STATE_KEY}${this._key}`;
  }

  /**
   * This is key for storing latest migration index in a storage.
   *
   * @readonly
   * @memberof Persist
   */
  get currentMigrationIndexKey() {
    return `${CURRENT_MIGRATION_INDEX}${this._key}`;
  }

  /**
   * Removes all the data from the storage includes persisted data and
   * the latest migration index
   */
  purge() {
    return LocalStorageService.multiRemove([
      this.key,
      this.currentMigrationIndexKey,
    ]);
  }

  /**
   * Fetches data from the storage and applies that snapshot to the store.
   * Updates store sate to the latest we have stored.
   * ATTENTION: It removes all other data from the store except persisted one
   */
  async rehydrate() {
    const snapshot = await this._getState();

    if (!snapshot) {
      return;
    }

    applySnapshot(this._store, this._deserialize(snapshot));
  }

  private async _getState() {
    const json = await LocalStorageService.getItem(this.key);

    if (!json) {
      return undefined;
    }

    const snapshot = JSON.parse(json);

    return snapshot;
  }

  private _setState(snapshot: any) {
    const json = JSON.stringify(snapshot);
    return LocalStorageService.setItem(this.key, json);
  }

  private _recordSnapshots() {
    onSnapshot(this._store, (snapshot) => {
      const serialized = this._serialize(snapshot);
      this._setState(serialized);
    });
  }

  private async _migratePersist(
    migrations: IMigration[] = [],
    startIndex: number,
  ) {
    const lastSnapshot = await this._getState();

    if (!lastSnapshot) {
      return;
    }

    const newSnapshot = migrations.reduce(
      (acc, { migrate }, index) => {
        if (startIndex < index && typeof migrate === 'function') {
          // only migrate to newer migrations
          acc = migrate(lastSnapshot);
        }

        return acc;
      },
      { ...lastSnapshot },
    );

    await this._setState(newSnapshot);
  }

  private async _getMigrationIndex() {
    const res = await LocalStorageService.getItem(
      this.currentMigrationIndexKey,
    );

    if (res === null) {
      return undefined;
    }

    const index = +res;

    return Number.isNaN(index) ? undefined : index;
  }

  private async _useLatestMigration() {
    const newMigrationIndex = this._migrations.length - 1;
    const state = await this._getState();
    const oldMigrationIndex = await this._getMigrationIndex();

    const shouldMigrate =
      typeof oldMigrationIndex !== 'undefined' &&
      oldMigrationIndex !== newMigrationIndex;

    if (shouldMigrate) {
      await this._migratePersist(this._migrations, oldMigrationIndex);
    }

    if (!!state && typeof oldMigrationIndex === 'undefined') {
      // we need to migrate from old app version
      await this._migratePersist(this._migrations, -1);
    }

    await LocalStorageService.setItem(
      this.currentMigrationIndexKey,
      String(newMigrationIndex),
    );

    return this._migrations[newMigrationIndex];
  }
}
